import { useState } from "react";
import { Plus, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useApi, useResource, errorText } from "@/lib/api";
import { formatMoney, formatAUD, sumDecimals } from "@/lib/format";
import RecordDialog from "@/components/RecordDialog";
import ConfirmDelete from "@/components/ConfirmDelete";

// List of misc balances or memo notes with an "Include in settlement" tick per row.
export default function IncludedList({ table, prefix, singular, fields, columns, blank, toBody, toForm, label, filterRow, onChange }) {
  const api = useApi();
  const { data, reload: load } = useResource(`/${table}`);
  const reload = () => { load(); onChange?.(); };
  const [editing, setEditing] = useState(null);
  const rows = (data || []).filter((r) => !filterRow || filterRow(r));
  const ticked = rows.filter((r) => r.include_in_settlement);

  const save = async (f) => {
    try {
      if (editing.id) await api("put", `/${table}/${editing.id}`, toBody(f));
      else await api("post", `/${table}`, toBody(f));
      toast.success(`${singular} saved`);
      setEditing(null);
      reload();
    } catch (e) { return errorText(e); }
  };
  const run = async (fn, msg) => { try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(errorText(e)); } };
  const tick = (r, v) => run(() => api("put", `/${table}/${r.id}`, { include_in_settlement: v }), v ? `${label(r)} included in settlement` : `${label(r)} excluded from settlement`);

  return (
    <div className="mt-8 space-y-5">
      <div className="grid grid-cols-2 gap-4 max-w-xl">
        <div className="border border-border bg-card p-4">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Total</div>
          <div className="font-mono text-lg mt-1" data-testid={`${prefix}-total`}>{formatAUD(sumDecimals(rows.map((r) => r.amount)))}</div>
        </div>
        <div className="border border-emerald-500/40 bg-emerald-500/5 p-4">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">In settlement ({ticked.length})</div>
          <div className="font-mono text-lg mt-1" data-testid={`${prefix}-included-total`}>{formatAUD(sumDecimals(ticked.map((r) => r.amount)))}</div>
        </div>
      </div>
      <div className="flex items-center">
        <p className="text-sm text-muted-foreground">Left out of the settlement unless ticked in.</p>
        <Button className="ml-auto" onClick={() => setEditing({ ...blank })} data-testid={`add-${prefix}-button`}><Plus className="h-4 w-4 mr-1" /> Add {singular.toLowerCase()}</Button>
      </div>
      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm" data-testid={`${prefix}-table`}>
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              {columns.map((c) => <th key={c.key} className="px-5 py-3 font-medium">{c.label}</th>)}
              <th className="px-5 py-3 font-medium text-right">Amount</th>
              <th className="px-5 py-3 font-medium">In settlement</th><th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className={`border-b border-border last:border-0 hover:bg-accent/30 transition-colors ${r.include_in_settlement ? "bg-emerald-500/5" : ""}`} data-testid={`${prefix}-row-${r.id}`}>
                {columns.map((c) => <td key={c.key} className="px-5 py-3">{c.render ? c.render(r) : r[c.key]}</td>)}
                <td className="px-5 py-3 text-right font-mono">{formatMoney(r.amount)}</td>
                <td className="px-5 py-3"><Switch checked={!!r.include_in_settlement} onCheckedChange={(v) => tick(r, v)} data-testid={`${prefix}-include-${r.id}`} /></td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditing(toForm(r))} data-testid={`${prefix}-edit-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                  <ConfirmDelete name={label(r)} testId={`${prefix}-delete-${r.id}`} onConfirm={() => run(() => api("delete", `/${table}/${r.id}`), `${singular} deleted`)} />
                </td>
              </tr>
            ))}
            {data && rows.length === 0 && <tr><td colSpan={columns.length + 3} className="px-5 py-8 text-center text-muted-foreground">Nothing here yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <RecordDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} prefix={`${prefix}-form`}
        title={editing?.id ? `Edit ${singular.toLowerCase()}` : `Add ${singular.toLowerCase()}`} fields={fields} initial={editing || blank} onSubmit={save} />
    </div>
  );
}
